import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { NAV_SECTIONS, TENANTS, type TenantKey } from './nav-config';
import { useApprovalCounts } from '@/hooks/useApprovals';
import { useConversations } from '@/hooks/useMessenger';
import { useNotices } from '@/hooks/useNotices';
import { useAuthStore } from '@/stores/auth-store';
import { setLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n';

export type RailMode = 'full' | 'compact';

interface NavRailProps {
  mode: RailMode;
  currentTenant: TenantKey;
  onNavigate?: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

const LANG_LABEL: Record<SupportedLocale, string> = {
  zh: 'login.lang_zh',
  'zh-TW': 'login.lang_zh_TW',
  en: 'login.lang_en',
  vi: 'login.lang_vi',
  km: 'login.lang_km',
};

export function NavRail({ mode, currentTenant, onNavigate }: NavRailProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const tenant = TENANTS.find((x) => x.key === currentTenant) ?? TENANTS[1];
  const railClass = mode === 'compact' ? 'rail rail-compact' : 'rail';
  const user = useAuthStore((s) => s.user);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const signOut = useAuthStore((s) => s.signOut);
  const { data: counts } = useApprovalCounts();
  const { data: convs = [] } = useConversations();
  const { data: notices = [] } = useNotices('all');
  const unreadMessages = convs.reduce((sum, c) => sum + (c.unread ?? 0), 0);
  const pendingApprovals = counts?.pending ?? 0;
  const unreadNotices = notices.filter((n) => n.unread).length;

  const [langOpen, setLangOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const langPopoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!langOpen) return;
    const onClick = (e: MouseEvent) => {
      if (langPopoverRef.current && !langPopoverRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [langOpen]);

  const currentLocale = (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as SupportedLocale)
    : 'en';

  const handleBell = () => {
    onNavigate?.();
    navigate('/notices');
  };

  const handleSettings = () => {
    onNavigate?.();
    navigate('/profile');
  };

  const handlePickLocale = (locale: SupportedLocale) => {
    setLocale(locale);
    setLangOpen(false);
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await signOut();
      onNavigate?.();
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error((err as Error).message ?? t('nav.sign_out'));
      setLoggingOut(false);
    }
  };

  const badgeFor = (source: string | undefined): number => {
    if (source === 'approvals') return pendingApprovals;
    if (source === 'messages') return unreadMessages;
    return 0;
  };

  return (
    <nav className={railClass} aria-label="Primary">
      <div className="rail-header">
        <div className="rail-logo">
          <div className="rail-logo-mark">
            <span className="material-symbols-outlined">print</span>
          </div>
          <div className="rail-logo-text">
            <span className="rail-logo-name">
              {t('common.app_name')}
              <span> {t('common.app_suffix')}</span>
            </span>
            <span className="rail-logo-sub">{t('common.app_sub')}</span>
          </div>
        </div>

        <div className="tenant-card tenant-card-static" aria-label={t('nav.current_tenant')}>
          <span className="tenant-badge" data-t={tenant.key}>
            {tenant.key}
          </span>
          <div className="tenant-info">
            <div className="tenant-name">{t(tenant.nameKey)}</div>
            <div className="tenant-code">{t(tenant.codeKey)}</div>
          </div>
        </div>
      </div>

      <div className="rail-scroll">
        {NAV_SECTIONS.map((section, idx) => {
          const visibleItems = section.items.filter(
            (item) => !item.permission || hasPermission(item.permission),
          );
          if (visibleItems.length === 0) return null;
          return (
            <div
              key={section.id}
              className={idx === NAV_SECTIONS.length - 1 ? 'rail-section rail-section-last' : 'rail-section'}
            >
              <div className="nav-section-label">{t(section.labelKey)}</div>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
                  onClick={onNavigate}
                  end={item.path === '/'}
                >
                  <span className="material-symbols-outlined nav-icon">{item.icon}</span>
                  <span className="nav-item-label">{t(item.labelKey)}</span>
                  {badgeFor(item.badgeSource) > 0 && (
                    <span className="nav-badge">{badgeFor(item.badgeSource)}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
      </div>

      <div className="rail-footer">
        <NavLink to="/profile" className="user-card" onClick={onNavigate}>
          <div className="user-avatar">{user ? initials(user.displayName) : 'GU'}</div>
          <div className="user-info">
            <div className="user-name">{user?.displayName ?? 'Guest'}</div>
            <div className="user-role">
              {user ? `${user.role} · ${user.tenant}` : t('nav.permission_locked')}
            </div>
          </div>
        </NavLink>
        <div className="util-row">
          <button
            type="button"
            className="util-btn"
            aria-label={t('nav.notifications')}
            onClick={handleBell}
          >
            <span className="material-symbols-outlined">notifications</span>
            {unreadNotices > 0 && <span className="util-btn-dot" />}
          </button>
          <div className="util-lang-wrap" ref={langPopoverRef}>
            <button
              type="button"
              className="util-btn"
              aria-label={t('nav.language')}
              aria-expanded={langOpen}
              aria-haspopup="menu"
              onClick={() => setLangOpen((v) => !v)}
            >
              <span className="material-symbols-outlined">translate</span>
            </button>
            {langOpen && (
              <div className="util-lang-popover" role="menu">
                {SUPPORTED_LOCALES.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    role="menuitemradio"
                    aria-checked={loc === currentLocale}
                    className={loc === currentLocale ? 'util-lang-item active' : 'util-lang-item'}
                    onClick={() => handlePickLocale(loc)}
                  >
                    <span className="util-lang-code">{t(LANG_LABEL[loc])}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="util-btn"
            aria-label={t('nav.settings')}
            onClick={handleSettings}
          >
            <span className="material-symbols-outlined">tune</span>
          </button>
          <button
            type="button"
            className="util-btn"
            aria-label={t('nav.sign_out')}
            onClick={handleLogout}
            disabled={loggingOut}
          >
            <span className="material-symbols-outlined">logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
}
